import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {BugsnagSeveritiesEnum} from '../../../../../libs/shared/core/src/helpers/bugsnag/bugsnagSeverities';
import {useConstant} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  ExtendedError,
  UnreachableError,
} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {isNonEmptyString} from '../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {IncidentModel} from '../../../../models/api/incidentModel';
import {makeIncidentConnectors} from '../../../../services/incidents';
import {
  IncidentsConnector,
  IncidentsUnavailableError,
} from '../../../../services/incidents/incidentsConnector';
import {cleanVisibleIncident, isIncidentVisible} from '../../../../services/incidents/incidentsHelpers';
import {StatusPageTypesEnum} from '../../../../services/incidents/statusPageIncidents';
import {getEnabledIntegrationTypes} from '../../../../settings/apps/pages/settingsAppsEnabledPageContainer';
import {getUserParticipatingChannels} from '../../../../store/data/channels/channelsSelectors';
import {getCompany} from '../../../../store/data/company/companySelectors';

/*
 * Constants.
 */

const initialFetchInterval = 10 * 1000; // First load: Wait 10 seconds before refreshing the next status page.
const activeIncidentRefreshInterval = 60 * 1000; // Incident in progress: Wait 1 minute before refreshing the next status page.
const noIncidentRefreshInterval = 2 * 60 * 1000; // No incident: Wait 2 minutes before refreshing the next status page.

/*
 * Hooks.
 */

/**
 * Alternately fetch across all our status pages (Front, Partners, Maintenance).
 * Will adapt refresh rate based on incident activity.
 *
 * @returns a list of active incidents.
 */
export function useFetchIncidentsPeriodically() {
  const [frontIncidents, setFrontIncidents] = useState<ReadonlyArray<IncidentModel>>([]);
  const [partnerIncidents, setPartnerIncidents] = useState<ReadonlyArray<IncidentModel>>([]);
  const [maintenanceIncidents, setMaintenanceIncidents] = useState<ReadonlyArray<IncidentModel>>([]);

  const [nextStatusPageFetch, setNextStatusPageFetch] = useState(StatusPageTypesEnum.DEFAULT);
  const [isPeriodicallyRefreshing, setIsPeriodicallyRefreshing] = useState(false);

  const ownLabels = useAppSelector(getOwnLabels);

  const visibleIncidents = useMemo(
    () =>
      [...frontIncidents, ...partnerIncidents, ...maintenanceIncidents]
        .filter((i) => isIncidentVisible(i, ownLabels))
        .map(cleanVisibleIncident),
    [frontIncidents, maintenanceIncidents, partnerIncidents, ownLabels],
  );

  const requestInterval = useMemo(() => {
    if (!isPeriodicallyRefreshing) {
      return initialFetchInterval;
    }

    return visibleIncidents.length > 0 ? activeIncidentRefreshInterval : noIncidentRefreshInterval;
  }, [visibleIncidents.length, isPeriodicallyRefreshing]);

  /*
   * Fetch incidents periodically.
   */

  const incidentConnectors = useConstant(makeIncidentConnectors);

  const fetchNextIncidentsAsync = useCallback(
    async (cancelToken: CancelToken) => {
      await delayAsync(requestInterval, cancelToken);

      switch (nextStatusPageFetch) {
        case StatusPageTypesEnum.DEFAULT: {
          const newIncidents = await fetchIncidentsAsync(incidentConnectors.defaultStatusPage, cancelToken);
          setFrontIncidents(newIncidents);
          setNextStatusPageFetch(StatusPageTypesEnum.PARTNER);
          break;
        }

        case StatusPageTypesEnum.PARTNER: {
          const newIncidents = await fetchIncidentsAsync(incidentConnectors.partnerStatusPage, cancelToken);
          setPartnerIncidents(newIncidents);
          setNextStatusPageFetch(StatusPageTypesEnum.MAINTENANCE);
          break;
        }

        case StatusPageTypesEnum.MAINTENANCE: {
          const newIncidents = await fetchIncidentsAsync(
            incidentConnectors.maintenanceStatusPage,
            cancelToken,
          );
          setMaintenanceIncidents(newIncidents);
          setNextStatusPageFetch(StatusPageTypesEnum.DEFAULT);

          // We've fetched everything once, now let's periodically update.
          setIsPeriodicallyRefreshing(true);
          break;
        }

        default:
          throw new UnreachableError(nextStatusPageFetch);
      }
    },
    [incidentConnectors, nextStatusPageFetch, requestInterval],
  );

  useEffect(() => {
    const cancelTokenSource = buildCancelTokenSource();
    fetchNextIncidentsAsync(cancelTokenSource.token);
    return () => cancelTokenSource.cancel();
  }, [fetchNextIncidentsAsync]);

  return visibleIncidents;
}

/*
 * Helpers.
 */

const getOwnLabels = createSelector(
  [getCompany, getUserParticipatingChannels, getEnabledIntegrationTypes],
  (company, participatingChannels, enabledIntegrationTypes) => {
    const accessibleChannelTypes = participatingChannels.map((channel) => channel.type);
    const accessibleIntegrations = enabledIntegrationTypes.map((integration) => integration.id);

    return _([
      company.region,
      company.shard,
      company.id.toString(),
      ...accessibleChannelTypes,
      ...accessibleIntegrations,
    ])
      .filter(isNonEmptyString)
      .map((label) => label.toLowerCase())
      .uniq()
      .value();
  },
);

async function fetchIncidentsAsync(connector: IncidentsConnector, cancelToken: CancelToken) {
  try {
    if (globalWindow.navigator.onLine) {
      return await connector.fetchIncidentsAsync(cancelToken);
    }
  } catch (error) {
    // Unless the component was unmounted, we maybe report.
    if (!(error instanceof CancelError)) {
      maybeReportFetchStatusPageError(error);
    }
  }

  return [];
}

function maybeReportFetchStatusPageError(error: any): void {
  // Don't report network errors and such to Bugsnag.
  if (error instanceof IncidentsUnavailableError) {
    return;
  }

  reportToBugsnag(new ExtendedError('Failed to fetch status page.', error), {
    severity: BugsnagSeveritiesEnum.INFO,
  });
}
